// src/contexts/WebSocketContext.jsx - ИСПРАВЛЕННАЯ ВЕРСИЯ С ОЧИСТКОЙ INACTIVE_REASON
import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from './AuthContext';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('disconnected');
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const subscriptions = useRef(new Map());
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;
  const { isAuthenticated, user } = useAuth();

  // Функция для проверки, активен ли аккаунт
  const isAccountActive = useCallback(() => {
    // Если пользователь авторизован, проверяем его статус
    if (user) {
      if (user.is_active === true) {
        // Если пользователь активен, очищаем устаревший флаг
        if (localStorage.getItem('inactive_reason')) {
          console.log('🧹 Cleaning stale inactive_reason - user is active');
          localStorage.removeItem('inactive_reason');
        }
        return true;
      } else {
        console.log('🔴 User object shows inactive:', user);
        return false;
      }
    }
    
    // Если пользователь не авторизован, проверяем localStorage
    const inactiveReason = localStorage.getItem('inactive_reason');
    if (inactiveReason) {
      try {
        const reason = JSON.parse(inactiveReason);
        const timestamp = new Date(reason.timestamp);
        const now = new Date();
        
        // Если прошло больше 5 минут, считаем что флаг устарел
        if (now - timestamp > 5 * 60 * 1000) {
          console.log('🧹 Removing expired inactive_reason');
          localStorage.removeItem('inactive_reason');
          return true;
        }
        return false;
      } catch {
        localStorage.removeItem('inactive_reason');
        return true;
      }
    }
    
    return true;
  }, [user]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User logout');
      wsRef.current = null;
    }
    setIsConnected(false);
    setConnectionState('disconnected');
    reconnectAttemptsRef.current = 0;
  }, []);

  const connect = useCallback((userId) => {
    disconnect();

    // Всегда сначала проверяем актуальный статус пользователя
    if (!isAccountActive()) {
      console.log('🔴 WebSocket: account is inactive, not connecting');
      setConnectionState('inactive');
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token || !isAuthenticated || !userId) {
      console.log('❌ WebSocket: no token or not authenticated');
      setConnectionState('disconnected');
      return;
    }

    // Используем wss:// в production
    const API_BASE_URL = window.location.hostname.includes('localhost')
      ? 'ws://localhost:8080'
      : `wss://${window.location.hostname}`;

    const wsUrl = `${API_BASE_URL}/api/v1/ws?token=${token}`;
    console.log('🔌 WebSocket connecting to', wsUrl);
    setConnectionState('connecting');

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('✅ WebSocket connected');
        setIsConnected(true);
        setConnectionState('connected');
        reconnectAttemptsRef.current = 0;

        // Подписываемся на общие каналы
        ws.send(JSON.stringify({
          type: 'subscribe',
          data: { channels: ['tasks:general', `user:${userId}`] }
        }));

        // Запускаем heartbeat
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 25000);
      };

      ws.onclose = (event) => {
        console.log('📤 WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        setConnectionState('disconnected');
        
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }

        // СПЕЦИАЛЬНАЯ ОБРАБОТКА ДЛЯ НЕАКТИВНОГО АККАУНТА
        if (event.code === 1008 && (
          event.reason.includes('inactive') || 
          event.reason.includes('User account is inactive')
        )) {
          console.log('🔴 WebSocket closed due to inactive account');
          
          localStorage.setItem('inactive_reason', JSON.stringify({
            message: 'Ваш аккаунт был деактивирован',
            timestamp: new Date().toISOString(),
            source: 'websocket'
          }));
          
          setConnectionState('inactive');
          
          if (window.confirm('Ваш аккаунт был деактивирован. Хотите перейти на страницу информации?')) {
            window.location.href = '/_admin/inactive';
          }
          return;
        }

        // Обычная логика переподключения
        if (isAuthenticated && event.code !== 1000 && isAccountActive()) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          console.log(`🔄 WebSocket reconnecting in ${delay}ms... (attempt ${reconnectAttemptsRef.current + 1}/${MAX_RECONNECT_ATTEMPTS})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
              reconnectAttemptsRef.current++;
              connect(userId);
            } else {
              console.log('❌ Max reconnection attempts reached');
              setConnectionState('failed');
            }
          }, delay);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'ping') {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'pong' }));
            }
            return;
          }
          
          if (message.type === 'account_deactivated') {
            console.log('🔴 Account deactivated message received:', message.data);
            
            localStorage.setItem('inactive_reason', JSON.stringify({
              message: message.data.message || 'Ваш аккаунт деактивирован',
              timestamp: new Date().toISOString(),
              source: 'websocket_message'
            }));
            
            window.dispatchEvent(new CustomEvent('account_deactivated', { 
              detail: message.data 
            }));
            
            disconnect();
            
            if (window.confirm(message.data.message || 'Ваш аккаунт деактивирован. Хотите перейти на страницу информации?')) {
              window.location.href = '/_admin/inactive';
            }
            return;
          }

          if (message.type === 'error') {
            console.error('WebSocket error from server:', message.message);
            return;
          }

          const callbacks = subscriptions.current.get(message.type);
          if (callbacks) {
            callbacks.forEach(cb => cb(message.data, message));
          } else {
            console.log('📨 Unhandled message type:', message.type);
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };
    } catch (error) {
      console.error('❌ WebSocket connection error:', error);
      setIsConnected(false);
      setConnectionState('disconnected');
    }
  }, [isAuthenticated, disconnect, isAccountActive]);

  const subscribe = useCallback((messageType, callback) => {
    if (!subscriptions.current.has(messageType)) {
      subscriptions.current.set(messageType, new Set());
    }
    subscriptions.current.get(messageType).add(callback);
    console.log(`📬 Subscribed to ${messageType}`);
  }, []);

  const unsubscribe = useCallback((messageType, callback) => {
    const callbacks = subscriptions.current.get(messageType);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        subscriptions.current.delete(messageType);
      }
    }
    console.log(`📭 Unsubscribed from ${messageType}`);
  }, []);

  const send = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  // Следим за изменением статуса пользователя
  useEffect(() => {
    if (user) {
      console.log('👤 User updated in WebSocket context:', {
        id: user.id,
        is_active: user.is_active,
        name: user.name
      });
      
      if (user.is_active === true) {
        if (localStorage.getItem('inactive_reason')) {
          console.log('🧹 User became active, clearing inactive_reason');
          localStorage.removeItem('inactive_reason');
        }
        
        if (!isConnected && connectionState === 'inactive' && isAuthenticated) {
          console.log('🔄 Attempting to reconnect after activation');
          connect(user.id);
        }
      }
    }
  }, [user?.is_active, user?.id, isConnected, connectionState, connect, isAuthenticated]);

  // Auto connect/disconnect based on auth
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      setTimeout(() => {
        connect(user.id);
      }, 100);
    } else {
      disconnect();
    }
    return () => {
      disconnect();
    };
  }, [isAuthenticated, user?.id, connect, disconnect]);

  const value = {
    isConnected,
    connectionState,
    subscribe,
    unsubscribe,
    send,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};